import { FC, useEffect, useState } from 'react'
import Lottie from 'lottie-react'
import { RenderIf } from 'components/RenderIf'

import { useTranslations } from 'featureSliced/shared/hooks/useTranslations'
import { Button } from 'featureSliced/entities/Button'
import { IPaymentComplete } from 'featureSliced/shared/api/qrMenu/types'
import { useOpenModal } from 'featureSliced/shared/hooks/useOpenModal'
import { EmailPrompt } from '../emailPrompt'
import { AnimationData } from 'featureSliced/shared/utils/animations'
import { loadAnimation } from 'featureSliced/shared/utils/loadAnimation'

import { ButtonContainer, ReceiptModalContainer } from '../styled'
import {
  AnimationContainer,
  MessageContainer,
  ReceiptNumberContainer,
} from './styled'
import { useMySelector } from 'featureSliced/shared/hooks/useMySelector'

type LoggedOutSuccessProps = {
  saveReceipt: () => Promise<void>
  isSaveLoading: boolean
  receiptData?: IPaymentComplete
  onCloseParentModal: (orderGuid?: string) => void
}

export const LoggedOutSuccess: FC<LoggedOutSuccessProps> = ({
  saveReceipt,
  isSaveLoading,
  receiptData,
  onCloseParentModal,
}) => {
  // Hooks
  const t = useTranslations()
  const openModal = useOpenModal()
  const appTheme = useMySelector((state) => state.app.interfaceStyles.appTheme)

  // State
  const [animationJson, setAnimationJson] = useState<AnimationData | null>(null)

  // useEffect
  useEffect(() => {
    const hasTips =
      Number(receiptData?.PaymentTipsAmount) -
        Number(receiptData?.PaymentAmount) >
      0
    const animationPath = hasTips
      ? `${appTheme}/smileyWithTips.json`
      : `${appTheme}/smileyDefault.json`

    loadAnimation(animationPath, (error) => {
      console.error('Failed to load smiley animation:', error)
    }).then(setAnimationJson)
  }, [receiptData?.PaymentTipsAmount, receiptData?.PaymentAmount])

  // Functions
  const openEmailPrompt = () => {
    openModal({
      id: 'EMAIL_PROMPT',
      title: '',
      components: [
        ({ closeThisModal }) => (
          <EmailPrompt
            onCloseModal={() => {
              onCloseParentModal?.(receiptData?.OrderGUID)
              closeThisModal()
            }}
          />
        ),
      ],
    })
  }

  return (
    <ReceiptModalContainer>
      <ReceiptNumberContainer>
        <RenderIf
          condition={!!receiptData?.receiptNr || !!receiptData?.orderNr}
        >
          <h2>{receiptData?.receiptNr || receiptData?.orderNr}</h2>
          <p>{t('receipt.number')}</p>
        </RenderIf>
      </ReceiptNumberContainer>
      <MessageContainer>
        <h2>{t('receipt.paymentSuccess')}</h2>
        <p>{t('receipt.thankYouForVisit')}</p>
      </MessageContainer>
      <AnimationContainer>
        <Lottie animationData={animationJson} loop={true} />
      </AnimationContainer>
      <ButtonContainer>
        <Button
          disabled={isSaveLoading}
          buttonType="stroke"
          width="100%"
          onClick={openEmailPrompt}
        >
          {t('buttons.sendEMail')}
        </Button>
        <Button
          isLoading={isSaveLoading}
          buttonType="common"
          width="100%"
          onClick={saveReceipt}
        >
          {t('buttons.saveAndClose')}
        </Button>
      </ButtonContainer>
    </ReceiptModalContainer>
  )
}
